import { format, getUnixTime, parseISO, Duration } from 'date-fns';
import * as uuid from 'uuid';
import { parsePhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input';
import type { PhotoModal } from 'Containers/PhotoViewCarousel/Models';
import * as Sentry from '@sentry/react';

export const isProduction = window.location.hostname === 'app.rocketplantech.com';
export const isLocal =
  import.meta.env.NODE_ENV !== 'production' && window.location.hostname === 'test.rocketplantech.com';

export const getFirstLetterUppercase = (value: string) => (value ? value.charAt(0).toLocaleUpperCase() : '');
export const convertFirstLetterUppercase = (value: string) =>
  value ? getFirstLetterUppercase(value) + value.slice(1) : '';
export const convertWordsFirstLetterUppercase = (value: string) => {
  if (!value) {
    return '';
  }

  const words = value.split(' ');

  if (words.length === 0) {
    return '';
  }

  return words.map((word: any) => convertFirstLetterUppercase(word)).join(' ');
};

export const formatDate = (dateString: string, dateFormat: string) => format(new Date(dateString), dateFormat);

// convert a date string in ISO 8601 format to a Date object
export const parseISODate = (dateString: string) => parseISO(dateString);

// workaround for Date object causing dates to be off by one due to unneeded timezone handling
// date come in from the backend as "2025-01-02T00:00:00.000Z", but becomes
// Wed Jan 01 2025 16:00:00 GMT-0800 (Pacific Standard Time) when converted to a date object
export const parseDateAsCalendarDate = (dateString: string): Date => {
  const dateStringNoTimezoneOffset = dateString.split('Z')[0];

  return new Date(dateStringNoTimezoneOffset);
};

// another workaround related to the issue above
// where a Date object with local timezone needs to converted to a timestamp in UTC to send to backend
export const formatAsISOCalendarDate = (date: Date): string => {
  const isoDateString = date.toISOString();
  const calendarDatePart = isoDateString.split('T')[0];
  return calendarDatePart.concat('T00:00:00.000000Z');
};

// another version of the above for when a Date object in local timezone needs to be sent to backend as a calendar date in UTC
export const formatAsCalendarDate = (date: Date): string => date.toISOString().split('T')[0];

export const getPhotosCount = (albumPhotosCount: number, internalPhotosCount: number) => {
  const result = albumPhotosCount - internalPhotosCount;

  return result !== -1 ? result : 0;
};

export const getUnixTimeFromString = (date: string) => getUnixTime(new Date(date));

export const compareTwoDates = (dateOne: string, dateTwo: string) =>
  getUnixTime(new Date(dateOne)) === getUnixTime(new Date(dateTwo));

export const convertPhoneNumber = (countryCode: string, phone: string) => {
  if (phone && countryCode) {
    let phoneAlt = phone.replace(countryCode, '');
    phoneAlt = phoneAlt.replace(/-/g, '');
    phoneAlt = phoneAlt.replace(/ /g, '');
    phoneAlt = phoneAlt.replace('+', '');

    // Dubai and Australian phone numbers can be 9 digits long
    switch (countryCode) {
      case '+751':
        if (phoneAlt.length < 9) {
          return '';
        }
        break;
      case '+61':
        if (phoneAlt.length < 9) {
          return '';
        }
        break;
      default:
        if (phoneAlt.length < 10) {
          return '';
        }
    }

    if (countryCode && phoneAlt) {
      const phoneNumber = parsePhoneNumber(countryCode + phoneAlt)?.number;
      return phoneNumber;
    }
  }
  return '';
};

export const formatPhoneInternationalWithCountryCode = (countryCode: string, phone: string) => {
  if (phone) {
    return formatPhoneNumberIntl(phone).replace(`${countryCode} `, '');
  }
  return '';
};

export const formatPhoneNumberInternational = (phone: string) => {
  if (phone) {
    return formatPhoneNumberIntl(phone);
  }
  return '';
};

export const formatPhone = (phone) => {
  if (phone) {
    const phoneAlt = phone.replace('+1', '');
    // const phoneAlt = parsePhoneNumber(phone).nationalNumber;
    const part1 = phoneAlt.length > 2 ? phoneAlt.substring(0, 3) : phoneAlt;
    const part2 = phoneAlt.length > 3 ? `-${phoneAlt.substring(3, 6)}` : '';
    const part3 = phoneAlt.length > 6 ? `-${phoneAlt.substring(6, 10)}` : '';

    return `${part1}${part2}${part3}`;
  }
  return '';
};

export const formatCurrency = (amount, currencyType) => {
  const formatter = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyType,
  });
  return formatter.format(amount);
};

export const getPhotoShareBreadCrumb = ({ photoable: room, albums }: PhotoModal, showAlbum = true) => {
  if (!room?.room_type) return '';

  const {
    room_type: { name: roomName },
    morphable,
    location,
  } = room;
  const unitName = morphable?.name ?? location?.name ?? '';

  // getting the first album
  const [album] = albums;

  let levelName = '';

  if (room.level && room.level?.name) {
    const { level } = room;
    levelName = level.name;
  }

  return `${unitName} / ${levelName ? `${levelName} / ` : ''} ${roomName} Photos ${
    showAlbum ? `${album?.name} / ` : ''
  }`;
};

export const getPhotoShareAlbum = ({ albums }: PhotoModal) => {
  if (albums.length === 0) {
    return '';
  }
  // getting the first album
  const [album] = albums;
  return `${album.name}`;
};

export const trimAndToLowerCase = (value: string) => value.replace(/\s/g, '').toLocaleLowerCase();

export const floorNumbers = (min = -25, max = 150, step = 1) =>
  Array.from({ length: (max - min) / step + 1 }, (_, i) => min + i).map((item) => ({
    id: item,
    name: `${item}`,
  }));

export const initials = (firstName, lastName) => getFirstLetterUppercase(firstName) + getFirstLetterUppercase(lastName);

export const validateImageFileType = (file) => !!(file.type === 'image/png' || file.type === 'image/jpeg');

export const validateImageResolution = (width, height) => !(width > 4000 || height > 4000);

// text limit for notes dropdown placeholder
export const limitText = (text: string, limit: number) => {
  if (text.length > limit) {
    return `${text.substring(0, limit)}...`;
  }
  return text;
};

// will get auth user details only if the routes are not match to below
export const shouldGetAuth = (pathname) =>
  pathname === '/' ||
  pathname.includes('/photo-share') ||
  pathname.includes('/reset-password') ||
  pathname.includes('/invite') ||
  pathname.includes('/signinemail') ||
  pathname.includes('/phoneverification') ||
  pathname.includes('/ghl/') || // GHL embedded routes use GHL SSO, not Laravel auth
  pathname === '/crm' || // GHL main embedded route uses GHL SSO, not Laravel auth
  /\/projects\/[0-9]+\/esignature\/[0-9]+/.test(pathname) ||
  pathname.includes('/phoneverificationcode') ||
  pathname.includes('/pdf-form-sign') ||
  pathname.includes('/photo-exports') ||
  pathname === '/support';

// add or remove an item from an array
export const addOrRemoveFromArray = (previousItems: any[], newItem: any) => {
  if (typeof newItem === 'object') {
    return previousItems.some((prevItem: any) => prevItem.id.toString() === newItem.id.toString())
      ? previousItems.filter((prevItem: any) => prevItem.id.toString() !== newItem.id.toString())
      : [...previousItems, newItem];
  }

  return previousItems.includes(newItem)
    ? previousItems.filter((prevId: any) => prevId.toString() !== newItem.toString())
    : [...previousItems, newItem];
};

export const getDisplayedRolesArray = (
  allRoles: any,
  excludedRoles = ['super-admin', 'employee'],
  roleRenameMapping = { 'company-admin': 'Admin' }
) => {
  const filteredRoles = allRoles.filter((role) => !excludedRoles.includes(role.name));

  if (filteredRoles.length === 0) {
    return [];
  }

  return filteredRoles.map((role) => ({
    id: role.id,
    name: roleRenameMapping[role.name] ?? role.display_name,
  }));
};

export const checkGalleryEnabled = (albumId: number, photoAlbums: any[]) =>
  photoAlbums?.some((album: any) => album.id === albumId && album.enabled === 1);

export const galleryShouldShow = (
  galleryId: number,
  selectedFilterIds: any[],
  galleryEnabled: boolean,
  isInEditMode: boolean
) => {
  if (selectedFilterIds.length === 0) {
    // only show all galleries in edit mode if the "all photos" filter is selected
    return galleryEnabled || isInEditMode;
  }

  return selectedFilterIds.includes(galleryId) && galleryEnabled;
};

export const chunkArray = (array, size) =>
  array.length <= size ? [array] : [array.slice(0, size), ...chunkArray(array.slice(size), size)];

// Transloadit is having issues with assemblies, disabling simultaneous uploads by increasing batch size to 50
// export const getPhotosChunkSize = (array) => {
//   if (array.length <= 20) {
//     return 10;
//   }
//   return Math.round(array.length / 2);
// };

export const getPhotosChunkSize = (array) => {
  if (array.length <= 100) {
    return 50;
  }
  return Math.round(array.length / 2);
};

export const checkIfPhotoSelected = (
  photo: any,
  selectedPhotos: any[],
  unSelectedPhotos: any[],
  selectPhotosMode: boolean,
  selectAllMode: boolean
) => {
  if (!selectPhotosMode) {
    return false;
  }

  if (selectAllMode) {
    return !unSelectedPhotos.some((item: any) => item.id === photo.id);
  }

  return selectPhotosMode && selectedPhotos.some((item: any) => item.id === photo.id);
};

export const generateUUID = () => uuid.v4();

export const compareArrays = (array1: any[], array2: any[]) => {
  if (array1.length !== array2.length) return false;
  for (let i = 0; i < array1.length; i += 1) {
    if (array1[i] !== array2[i]) return false;
  }
  return true;
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getTimeout = (length: number) => {
  if (length < 3) {
    return 100;
  }

  if (length < 5) {
    return 300;
  }

  if (length < 10) {
    return 1500;
  }

  return 3000;
};

const getSafeFileName = (name: string) => {
  if (!name) return 'download';

  const parts = name.split('.');
  if (parts.length === 1) return name;
  const extension = parts.pop();
  const base = parts.join('.');

  // Replace dots in the base name but keep the final extension dot intact
  return `${base.replace(/\./g, '_')}.${extension}`;
};

// download a pdf, or doc etc...
export const download = async (url: string, fileName: string, setLoading: any = undefined) => {
  try {
    // Validate URL before attempting download
    if (!url) {
      const error = new Error('Download failed: URL is empty or undefined');
      Sentry.captureException(error, {
        tags: { feature: 'file-download' },
        extra: { fileName, url },
      });
      throw error;
    }

    const response = await fetch(url);

    // Check if response is ok (status 200-299)
    if (!response.ok) {
      const error = new Error(`Download failed: HTTP ${response.status} ${response.statusText}`);
      Sentry.captureException(error, {
        tags: { feature: 'file-download' },
        extra: {
          fileName,
          url,
          status: response.status,
          statusText: response.statusText,
        },
      });
      throw error;
    }

    const blob = await response.blob();

    // Validate blob size
    if (blob.size === 0) {
      const error = new Error('Download failed: File is empty (0 bytes)');
      Sentry.captureException(error, {
        tags: { feature: 'file-download' },
        extra: { fileName, url, blobSize: blob.size },
      });
      throw error;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = blobUrl;
    link.setAttribute('download', getSafeFileName(fileName)); // preserve extension while normalizing interior dots

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);

    // Revoke the blob URL to free memory
    window.URL.revokeObjectURL(blobUrl);

    // clear loading if given
    if (setLoading) setLoading(false);
  } catch (error: any) {
    if (setLoading) setLoading(false);

    // Log to Sentry if not already logged above
    if (!error.message?.startsWith('Download failed:')) {
      Sentry.captureException(error, {
        tags: { feature: 'file-download' },
        extra: { fileName, url, errorMessage: error.message },
      });
    }

    alert('Something went wrong. Unable to download the file. Please try again later.');
  }
};

// download an API return value as a file
export const downloadLocal = async (returnedContent: string, fileName: string, setLoading: any = undefined) => {
  try {
    const file = new Blob([returnedContent], { type: 'text/csv;charset=UTF-8' });

    const blobUrl = window.URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = blobUrl;
    link.setAttribute('download', getSafeFileName(fileName));

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);

    // Revoke the blob URL to free memory
    window.URL.revokeObjectURL(blobUrl);

    // clear loading if given
    if (setLoading) setLoading(false);
  } catch (errors: any) {
    if (setLoading) setLoading(false);
    Sentry.captureException(errors, {
      tags: { feature: 'file-download' },
      extra: { fileName, errorMessage: errors?.message },
    });
    alert('Something went wrong. Unable download the file. Please try again later.');
  }
};

export const addDurations = (duration1: Duration, duration2: Duration): Duration => {
  const sumDuration: Duration = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  if (duration1.years || duration2.years) {
    sumDuration.years = (duration1.years ?? 0) + (duration2.years ?? 0);
  }
  if (duration1.months || duration2.months) {
    sumDuration.months = (duration1.months ?? 0) + (duration2.months ?? 0);
  }
  if (duration1.weeks || duration2.weeks) {
    sumDuration.weeks = (duration1.weeks ?? 0) + (duration2.weeks ?? 0);
  }
  if (duration1.days || duration2.days) {
    sumDuration.days = (duration1.days ?? 0) + (duration2.days ?? 0);
  }
  if (duration1.hours || duration2.hours) {
    sumDuration.hours = (duration1.hours ?? 0) + (duration2.hours ?? 0);
  }
  if (duration1.minutes || duration2.minutes) {
    sumDuration.minutes = (duration1.minutes ?? 0) + (duration2.minutes ?? 0);
  }
  if (duration1.seconds || duration2.seconds) {
    sumDuration.seconds = (duration1.seconds ?? 0) + (duration2.seconds ?? 0);
  }

  if (sumDuration.seconds >= 60) {
    sumDuration.seconds -= 60;
    sumDuration.minutes += 1;
  }

  if (sumDuration.minutes >= 60) {
    sumDuration.minutes -= 60;
    sumDuration.hours += 1;
  }

  if (sumDuration.hours >= 24) {
    sumDuration.hours -= 24;
    sumDuration.days += 1;
  }

  return sumDuration;
};
